import { useEffect, useState } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import Loader from '../../common/Loader';
import { useAuth } from '../../../context/AuthContext';
import { dictionary } from '../../../utils/dictionary';
import { SalesAngle } from '../../../types/onboarding';
import { Clock } from 'lucide-react';
import { useTutorialContext } from '../../../context/TutorialContext';
export function StepSalesAngles() {
  const { language } = useAuth();
  const { data, updateVisualContent, salesAngles, setSalesAngles, isFetchingSalesAngleRecommendation, hasFetchedSalesAngleRecommendation, salesAngleRecommendation } = useOnboardingStore();
  const { visualContent } = data;
  const [selectedAngles, setSelectedAngles] = useState<string[]>(
    visualContent.selectedAngle.map(a => a.name)
  );

  // Sync local state when AI recommendation auto-selects an angle
  useEffect(() => {
    if (hasFetchedSalesAngleRecommendation && visualContent.selectedAngle.length > 0) {
      setSelectedAngles(visualContent.selectedAngle.map(a => a.name));
    }
  }, [hasFetchedSalesAngleRecommendation, visualContent.selectedAngle]);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customAngleText, setCustomAngleText] = useState(visualContent.customAngle || '');
  const { setCurrentStepIndex, resumeTutorial, isPaused } = useTutorialContext();

  const toggleAngle = (angleId: string) => {
    const isSelected = selectedAngles.includes(angleId);
    let newSelected: string[];

    if (isSelected) {
      newSelected = selectedAngles.filter(id => id !== angleId);
    } else {
      if (selectedAngles.length < 6) {
        newSelected = [...selectedAngles, angleId];
      } else {
        return;
      }
    }

    setSelectedAngles(newSelected);
    updateVisualContent({
      selectedAngle: newSelected.map(id => {
        const angle = salesAngles.find(a => a.id === id);
        return {
          name: id,
          description: angle?.description || id
        };
      })
    });
  };

  const handleCustomAngleClick = () => {
    setCustomAngleText('');
    setShowCustomInput(!showCustomInput);
  };

  const handleCustomAngleChange = (value: string) => {
    setCustomAngleText(value);
    setSalesAngles([...salesAngles, { id: value, title: value, description: value }] as SalesAngle[]);
    updateVisualContent({
      customAngle: value,
      selectedAngle: [...visualContent.selectedAngle, { name: value, description: value }]
    });
    setShowCustomInput(false);
    setSelectedAngles([...selectedAngles, value]);
  };

  useEffect(() => {
    if (salesAngles.length > 0 && isPaused) { 
      setCurrentStepIndex(4);
      resumeTutorial();
    }
  }, [salesAngles]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {dictionary["select-one-or-more-sales-angles"][language]}
        </h1>
        <p className="text-sm font-medium text-[#64748B]">
          {dictionary["we-will-create-a-page-with-each-selected-sales-angle"][language]}
        </p>
      </div>
      {salesAngles.length === 0 || isFetchingSalesAngleRecommendation ? <Loader size={40} /> : (
        <>
          <div className="space-y-3 max-w-3xl mx-auto">
            {salesAngles.map((angle: SalesAngle, index: number) => {
              const isSelected = selectedAngles.includes(angle?.id);
              const Icon = angle?.icon;

              return (
                <button
                  id={index === 0 ? `salesAngles-target` : ``}
                  key={angle?.id}
                  onClick={() => toggleAngle(angle?.id)}
                  className={`w-full px-5 py-3 rounded-[25px] border-[1px] border-[#F1F5F9] transition-all text-left ${isSelected
                    ? 'border-[#3e3eff] bg-[#F5F8FF]'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#DAE4FF]' : 'bg-gray-100'
                      }`}>
                      {Icon ? <Icon className={`w-6 h-6 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} /> : <Clock className={`w-6 h-6 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />}
                    </div>
                    <p className="text-sm font-extrabold text-gray-600 flex-1">
                      {angle?.title}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="max-w-3xl mx-auto mt-4">
            {/* Botón para abrir input */}
            {!showCustomInput && (
              <button
                onClick={handleCustomAngleClick}
                className="ml-2 text-sm font-extrabold text-[#3E3EFF] hover:text-[#2d3a7f]"
              >
                + {dictionary["enter-custom-sales-angle"][language]}
              </button>
            )}

            {showCustomInput && (
              <div className="mt-4 flex items-center gap-2">

                <input
                  type="text"
                  value={customAngleText}
                  onChange={(e) => setCustomAngleText(e.target.value)}
                  placeholder={dictionary["describe-your-custom-sales-angle-placeholder-manual-entry"][language]}
                  className="w-full px-4 py-3 border-2 border-[#3E3EFF] rounded-xl focus:border-[#3B4A9F] focus:outline-none text-sm"
                />

                <button
                  onClick={() => handleCustomAngleChange(customAngleText)}
                  disabled={!customAngleText.trim()}
                  className={
                    `px-4 py-3 rounded-xl text-white text-sm font-bold transition-all
                    ${customAngleText.trim()
                      ? "bg-[#3E3EFF] hover:bg-[#2d3aee]"
                      : "bg-[#c6ccff] cursor-not-allowed"
                    }
                  `}
                >
                  {dictionary["add"][language]}
                </button>

                <button
                  onClick={() => {
                    setShowCustomInput(false);
                    setCustomAngleText("");
                  }}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-xl"
                >
                  <span className="text-gray-600 text-xl">×</span>
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

